import {
  amountsMatch,
  normalizeApplicationDetails,
} from "./applicationCostHelpers";

export {
  normalizeApplicationDetails,
  getInitialCostStateFromDetails,
  getEffectiveBaseCost,
  getEffectiveCurrency,
  getEffectiveTax,
} from "./applicationCostHelpers";

export function findGstSettingByCurrency(gstSettings, currencyCode) {
  if (!currencyCode || !Array.isArray(gstSettings)) return null;

  const normalized = String(currencyCode).trim().toUpperCase();

  return (
    gstSettings.find(
      (setting) =>
        String(setting.currency_code || "")
          .trim()
          .toUpperCase() === normalized
    ) || null
  );
}

export function formatGstAmount(value) {
  const num = Number(value);
  if (Number.isNaN(num)) return "0.00";
  return num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

const roundMoney = (value) => Math.round(Number(value) * 100) / 100;

/**
 * User-entered amount is always base_cost.
 * Inclusive: amount includes GST → GST = Amount × Rate / (100 + Rate),
 *   professional_cost = base_cost.
 * Exclusive: GST is added on top → GST = Amount × Rate / 100,
 *   professional_cost = base_cost + gst_amount.
 * No tax: gst fields are zero → professional_cost = base_cost.
 */
export function calculateGstBreakdown({ baseCost, gstPercentage, taxType }) {
  const amount = Number(baseCost);
  const rate = Number(gstPercentage);

  if (!amount || amount <= 0) {
    return {
      base_cost: amount || 0,
      gst_percentage: 0,
      gst_amount: 0,
      professional_cost: amount || 0,
      taxType: taxType || "",
    };
  }

  if (!taxType || taxType === "notax" || !rate || rate <= 0) {
    return {
      base_cost: amount,
      gst_percentage: 0,
      gst_amount: 0,
      professional_cost: amount,
      taxType: taxType || "notax",
    };
  }

  if (taxType === "inclusive") {
    const gst_amount = roundMoney((amount * rate) / (100 + rate));
    return {
      base_cost: amount,
      gst_percentage: rate,
      gst_amount,
      professional_cost: amount,
      taxType,
    };
  }

  const gst_amount = roundMoney((amount * rate) / 100);

  return {
    base_cost: amount,
    gst_percentage: rate,
    gst_amount,
    professional_cost: roundMoney(amount + gst_amount),
    taxType,
  };
}

export function resolveApplicationCostBreakdown({
  baseCostInput,
  currency,
  taxType,
  gstSetting,
  savedDetails,
}) {
  const saved = normalizeApplicationDetails(savedDetails);
  const settingRate = Number(gstSetting?.gst_percentage || 0);
  const savedRate = Number(saved.gst_percentage || 0);
  const amount = Number(baseCostInput);
  const effectiveTax = taxType || saved.gst_status || "";
  const inputIsEmpty =
    baseCostInput === "" ||
    baseCostInput === null ||
    baseCostInput === undefined;

  if (inputIsEmpty) {
    return {
      ...calculateGstBreakdown({
        baseCost: 0,
        gstPercentage: 0,
        taxType: effectiveTax,
      }),
      fromSaved: false,
    };
  }

  const hasStoredBreakdown =
    saved.base_cost !== null &&
    saved.base_cost !== undefined &&
    saved.base_cost !== "" &&
    saved.gst_percentage !== null &&
    saved.gst_percentage !== undefined &&
    saved.gst_percentage !== "" &&
    saved.gst_amount !== null &&
    saved.gst_amount !== undefined &&
    saved.gst_amount !== "";

  const currencyMatches = amountsMatch(
    currency || saved.currency,
    saved.currency
  );
  const taxMatches = effectiveTax === saved.gst_status;
  const costMatches = amountsMatch(
    baseCostInput,
    saved.base_cost ?? saved.professional_cost
  );

  if (
    hasStoredBreakdown &&
    currencyMatches &&
    taxMatches &&
    costMatches &&
    (effectiveTax === "inclusive" || effectiveTax === "exclusive")
  ) {
    return {
      ...calculateGstBreakdown({
        baseCost: Number(saved.base_cost),
        gstPercentage: Number(saved.gst_percentage),
        taxType: effectiveTax,
      }),
      fromSaved: true,
    };
  }

  const effectiveBase =
    amount > 0
      ? amount
      : Number(saved.base_cost ?? saved.professional_cost ?? 0);

  const effectiveRate =
    hasStoredBreakdown && currencyMatches && taxMatches && savedRate > 0
      ? savedRate
      : settingRate;

  return {
    ...calculateGstBreakdown({
      baseCost: effectiveBase,
      gstPercentage: effectiveRate,
      taxType: effectiveTax,
    }),
    fromSaved: false,
  };
}

export function buildApplicationCostPayload(breakdown) {
  const base_cost = breakdown?.base_cost ?? 0;
  const professional_cost = breakdown?.professional_cost ?? base_cost;

  return {
    base_cost,
    gst_percentage: breakdown?.gst_percentage ?? 0,
    gst_amount: breakdown?.gst_amount ?? 0,
    professional_cost,
    cost: professional_cost,
  };
}

export function buildDepartmentFeeCostPayload(breakdown, taxStatus = "") {
  const base_cost = breakdown?.base_cost ?? 0;
  const department_fee = breakdown?.professional_cost ?? base_cost;

  return {
    department_base_fee: base_cost,
    department_fee_gst_percentage: breakdown?.gst_percentage ?? 0,
    department_fee_gst_amount: breakdown?.gst_amount ?? 0,
    department_fee_gst_status: taxStatus || breakdown?.taxType || "",
    department_fee,
  };
}
